import { useCallback, useEffect, useMemo } from "react";
import { useMessage } from "../../../hooks/useMessage";
import { useProductColors } from "../../../hooks/useProductColors";
import { useUsedTextForegroundColors } from "../../../hooks/useUsedTextForegroundColors";

let update = true;

let colorsToShowInPanel: eXpress.core.IColor[] = [];
let unsavedColors: eXpress.core.IColor[] = [];
const savedColors: eXpress.core.IColor[] = [];

/**
 * A list of all possible text colors to choose from
 * - used colors in the design (from configuration and definition)
 * - product colors from server
 * When the panel unmounts, the last selected color is saved and added to the list of colors, \
 * so it can be selected again when opening the panel another time, even if it is not in the used colors or product colors anymore \
 * (example when changing definition, we remember them)
 */
export function useTextColors() {
	const { data: productColors = [] } = useProductColors();
	const usedColors = useUsedTextForegroundColors({
		fromConfiguration: true,
		fromDefinition: true,
	});

	// get the initial colors to show
	useMemo(() => {
		if (update) {
			// eslint-disable-next-line react-compiler/react-compiler
			colorsToShowInPanel = [...usedColors, ...productColors].distinct(
				(color) => color.hexColorString,
			);
		}
	}, [usedColors, productColors]);

	// update colors to show when panel unmounts
	useEffect(() => {
		return () => {
			if (!unsavedColors.length) return;
			// Add latest selected color to the beginning of the saved colors array
			savedColors.unshift(unsavedColors[unsavedColors.length - 1]);
			// filter out duplicates
			colorsToShowInPanel = [...savedColors, ...colorsToShowInPanel].distinct(
				(color) => color.hexColorString,
			);

			unsavedColors = [];
		};
	}, []);

	useMessage(
		useCallback((message) => {
			update = false;
			if (message instanceof eXpress.core.TextColorChangeMessage) {
				message.textZones.forEach((textZone) => {
					unsavedColors.push({
						hexColorString: eXpress.HexColorString.fromString(
							textZone.textSettings.foregroundColor.webColor,
						),
						colorStyle: textZone.colorStyle,
					});
				});
			}
		}, []),
	);

	return colorsToShowInPanel;
}
